import { get } from "lodash";
import { ChevronUp, Download, Filter } from "lucide-react";
import PropTypes from "prop-types";
import React, {
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
  lazy,
} from "react";
import { useDataProvider, useTranslate } from "react-admin";
import { useNodePage } from "../NodePageContext";
import { useSharedTableComponent } from "./tableComponent/SharedTableComponent";

const TableFilters = lazy(() => import("./TableFilters"));
const TableHeader = lazy(() => import("./TableHeader"));
const TablePaginator = lazy(() => import("./TablePaginator"));
const TableRow = lazy(() => import("./TableRow"));

const ROWS_PER_PAGE = 10;

const useCreateTable = (data) => {
  return useMemo(() => {
    const headers = get(data, "[0].table.headers", []);
    const rows = data.reduce(
      (acc, item) => acc.concat(get(item, "table.rows", [])),
      [],
    );
    const filters = headers.filter((h) => h.is_filterable);
    const searchable = data.some((d) => d.table.is_searchable);
    const exportable = data.some((d) => d.table.is_exportable);
    return { headers, rows, filters, searchable, exportable };
  }, [data]);
};

const TableRows = ({ totalRows, data, showAsResult = false, node }) => {
  const translate = useTranslate();
  const { newParams, setNewParams } = useNodePage() || {
    newParams: {},
    setNewParams: () => {},
  };
  const [expanded, setExpanded] = useState([]);
  const [currentPage, setCurrentPage] = useState(newParams.page || 1);
  const [filterValues, setFilterValues] = useState(newParams.filter || []);
  const [showFilters, setShowFilters] = useState(filterValues.length > 0);
  const [sort, setSort] = useState(newParams.sort);
  const [direction, setDirection] = useState(newParams.dir);
  const dataProvider = useDataProvider();

  const {
    exportTable,
    modernTableStyles,
    ID_COMMON_TABLE,
    responsiveTableWrap,
    tableBase,
  } = useSharedTableComponent();

  const [isExporting, setIsExporting] = useState(false);

  const exportingRef = useRef(false);

  const { headers, rows, filters, searchable, exportable } =
    useCreateTable(data);

  const handleExportError = useCallback((message) => {
    alert(message);
    setIsExporting(false);
    exportingRef.current = false;
  }, []);

  const columnsData = useMemo(
    () => ({
      columns: headers.filter((h) => h.is_preview),
      expandable: headers.filter((h) => !h.is_preview),
    }),
    [headers],
  );

  const paginationData = useMemo(
    () => ({
      pages: Math.ceil(totalRows / ROWS_PER_PAGE),
      hasMultiplePages: Math.ceil(totalRows / ROWS_PER_PAGE) > 1,
    }),
    [totalRows],
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      setNewParams({ page, offset: (page - 1) * ROWS_PER_PAGE });
    },
    [setNewParams],
  );

  const handleExport = useCallback(async () => {
    if (exportingRef.current || isExporting) return;

    exportingRef.current = true;
    setIsExporting(true);
    await exportTable(
      "website",
      newParams,
      handleExportError,
      dataProvider,
      ID_COMMON_TABLE,
    );
    setIsExporting(false);
    exportingRef.current = false;
  }, [
    isExporting,
    exportTable,
    newParams,
    handleExportError,
    dataProvider,
    ID_COMMON_TABLE,
  ]);

  const handleFiltersToggle = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const normalizeFilter = useCallback((filter) => {
    return [...filter].sort((a, b) => {
      if ("*" in a) return -1;
      if ("*" in b) return 1;
      return 0;
    });
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters) => {
      const normalizedValue = normalizeFilter(newFilters);
      setFilterValues(normalizedValue);
      setNewParams({ filter: normalizedValue });
      if (currentPage !== 1) {
        handlePageChange(1);
      }
    },
    [normalizeFilter, setNewParams, currentPage, handlePageChange],
  );
  const handleFiltersClear = useCallback(() => {
    setFilterValues([]);
    setNewParams({ filter: [] });
    if (currentPage !== 1) {
      handlePageChange(1);
    }
  }, [setNewParams, currentPage, handlePageChange]);

  const toggleRow = useCallback((index, e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded((prev) => {
      if (prev.includes(index)) {
        return prev.filter((item) => item !== index);
      } else {
        return [...prev, index];
      }
    });
  }, []);

  const handleSort = useCallback(
    (id, dir) => {
      const order = `${id}:${dir}`;
      setSort(id);
      setDirection(dir);
      setNewParams({ order, sort: id, dir });
      if (currentPage !== 1) {
        handlePageChange(1);
      }
    },
    [setNewParams, currentPage, handlePageChange],
  );

  if (showAsResult) {
    return null;
  }

  if (totalRows === 0) {
    return null;
  }

  const shouldShowFiltersCard =
    !showAsResult && searchable && filters.length > 0;
  const shouldShowExportButton = !showAsResult && !searchable && exportable;
  const hasExpandableColumns = columnsData.expandable.length > 0;
  const hasNoRows = rows.length === 0;
  const shouldShowPagination = !showAsResult && paginationData.hasMultiplePages;

  return (
    <div style={modernTableStyles.mainContainer}>
      {/* Filtri */}
      {shouldShowFiltersCard && (
        <div style={modernTableStyles.card}>
          <div style={modernTableStyles.cardHeader}>
            <button
              style={modernTableStyles.filtersToggle}
              onClick={handleFiltersToggle}
              className="hoverSortHeader"
            >
              <Filter size={20} />
              <span>{showFilters ? "Nascondi filtri" : "Mostra filtri"}</span>
              {showFilters ? (
                <ChevronUp className="is-collapsed" size={16} />
              ) : (
                <ChevronUp className="is-expanded" size={16} />
              )}
            </button>
          </div>

          {showFilters && (
            <div style={modernTableStyles.cardBody}>
              <Suspense fallback={<div>Loading filters...</div>}>
                <TableFilters
                  searchable={searchable}
                  exportable={exportable}
                  filters={filters}
                  node={node}
                  onExport={handleExport}
                  onChange={handleFiltersChange}
                  onClear={handleFiltersClear}
                  filterValues={filterValues}
                />
              </Suspense>
            </div>
          )}
        </div>
      )}

      {/* Pulsante Export */}
      {shouldShowExportButton && (
        <button
          style={modernTableStyles.exportButton}
          onClick={handleExport}
          disabled={isExporting}
          onMouseEnter={(e) => {
            if (!isExporting) {
              e.target.style.backgroundColor = "#1e3a8a";
              e.target.style.transform = "translateY(-1px)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isExporting) {
              e.target.style.backgroundColor = "#1e40af";
              e.target.style.transform = "translateY(0)";
            }
          }}
        >
          <Download size={16} />
          {isExporting ? "Esportazione..." : "Esporta"}
        </button>
      )}

      {/* Tabella */}
      <div style={modernTableStyles.card}>
        {hasNoRows && !showAsResult ? (
          <div style={modernTableStyles.noResultsContainer}>
            <div style={modernTableStyles.noResultsText}>
              Nessun risultato trovato
            </div>
            {shouldShowFiltersCard && (
              <div style={modernTableStyles.noResultsSubtext}>
                Prova a modificare i filtri di ricerca
              </div>
            )}
          </div>
        ) : (
          <div className="tableScrollX">
            <table style={{ ...modernTableStyles.table, ...tableBase }}>
              <thead style={modernTableStyles.tableHeader}>
                <tr>
                  {hasExpandableColumns && (
                    <th style={{ width: "50px", padding: "1rem 1rem" }}></th>
                  )}
                  {columnsData.columns.map((column) => (
                    <Suspense
                      key={get(column, "id")}
                      fallback={<th>Loading...</th>}
                    >
                      <TableHeader
                        column={column}
                        node={node}
                        onSort={handleSort}
                        sort={sort}
                        direction={direction}
                      />
                    </Suspense>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <Suspense
                    key={index}
                    fallback={
                      <tr>
                        <td>Loading...</td>
                      </tr>
                    }
                  >
                    <TableRow
                      columns={columnsData.columns}
                      expandable={columnsData.expandable}
                      expanded={expanded}
                      toggleRow={toggleRow}
                      index={index}
                      row={row}
                    />
                  </Suspense>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info paginazione e controlli */}
        {!showAsResult && (
          <>
            {!hasNoRows && (
              <div style={modernTableStyles.paginationInfo}>
                Pagina {currentPage} di {paginationData.pages} - Totale:{" "}
                {totalRows === 1 && rows?.length === 0
                  ? rows.length
                  : totalRows}{" "}
                risultati
              </div>
            )}

            {shouldShowPagination && (
              <Suspense fallback={<div>Loading pagination...</div>}>
                <TablePaginator
                  pages={paginationData.pages}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              </Suspense>
            )}
          </>
        )}
      </div>
    </div>
  );
};

TableRows.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      table: PropTypes.shape({
        headers: PropTypes.arrayOf(
          PropTypes.shape({
            def_type: PropTypes.string.isRequired,
            is_filterable: PropTypes.bool,
            is_preview: PropTypes.bool,
            name: PropTypes.string.isRequired,
            order_index: PropTypes.number.isRequired,
          }),
        ),
        rows: PropTypes.arrayOf(
          PropTypes.objectOf(
            PropTypes.shape({
              raw_value: PropTypes.string,
            }),
          ),
        ),
      }),
    }),
  ).isRequired,
  totalRows: PropTypes.number.isRequired,
  showAsResult: PropTypes.bool,
  node: PropTypes.object,
  newParams: PropTypes.object,
  setNewParams: PropTypes.func,
};

export default React.memo(TableRows);
